import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, Signal } from '@angular/core';
import { ButtonModule, SelectModule, InputModule, TextareaModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    InvestigationReviewDomesticForm,
    InvestigationReviewDomesticFormGroup
} from '../../../models/cod-review.model';
import { CodReviewOperations } from '../../../operations/cod-review.operations';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { CodReviewState } from '../../../state/cod-review.state';
import { select, Store } from '@ngxs/store';
import { SubmitInvestigationReviewDomestic } from '../../../state/cod-review.action';
import { distinctUntilChanged, filter, tap } from 'rxjs';
import { config } from './investigation-review-domestic.config';
import {
    ReviewResult,
    NatureOfComplaintDomestic,
    RootCause,
    Through,
    BasicReviewResult,
    ReviewNextStepClose
} from '@cod-monorepo/interfaces';
import { CodReviewSuccessComponent } from '../review-success/review-success.component';
import { CsrResponsibleSelectComponent } from '../../../../shared/components/csr-responsible-select/csr-responsible-select.component';
import { NgxsFormDirective } from '@ngxs/form-plugin';

@Component({
    selector: 'app-cod-investigation-review-domestic-step',
    templateUrl: './investigation-review-domestic.component.html',
    imports: [
        CommonModule,
        SelectModule,
        FormsModule,
        ReactiveFormsModule,
        InputModule,
        ButtonModule,
        AttachmentsInputComponent,
        TextareaModule,
        CodReviewSuccessComponent,
        CsrResponsibleSelectComponent,
        NgxsFormDirective
    ],
    standalone: true
})
export class CodInvestigationReviewDomesticStepComponent implements OnInit, OnChanges {
    @Input() disabled: boolean;

    protected submitting: Signal<boolean> = select(CodReviewState.submittingInvestigationReviewDomestic);
    protected submittingSuccess: Signal<boolean> = select(CodReviewState.submittingInvestigationReviewSuccessDomestic);
    public formGroup: FormGroup<InvestigationReviewDomesticFormGroup>;
    public options: string[] = [];
    public label = '';
    public natureOfComplaint: string[] = [];
    public throughOptions = Through;
    public basicReviewResult = BasicReviewResult;
    public reviewResult = ReviewResult;
    public rootCause = RootCause;
    public nextSteps = ReviewNextStepClose;

    constructor(private store: Store) {}

    public ngOnInit(): void {
        this.formGroup = CodReviewOperations.initializeInvestigationReviewDomesticForm();
        this.formGroup.controls.issueType.disable();
        this.initRevisedNatureOfComplaintChange();

        if (this.disabled) {
            this.formGroup.disable();
            this.natureOfComplaint = Object.values(NatureOfComplaintDomestic);
        } else {
            // Note: Billing adjustment was removed, keep for compatibility with old cases
            this.natureOfComplaint = Object.values(NatureOfComplaintDomestic).filter(
                noc => noc !== NatureOfComplaintDomestic.BILLING_ADJUSTMENT
            );
        }
    }

    public ngOnChanges(): void {
        if (this.disabled) {
            this.formGroup && this.formGroup.disable();
        } else {
            this.formGroup && this.formGroup.enable();
        }
    }

    public submit(): void {
        if (this.formGroup.invalid) {
            return;
        }

        this.store.dispatch(
            new SubmitInvestigationReviewDomestic(this.formGroup.value as InvestigationReviewDomesticForm)
        );
        this.formGroup.disable();
    }

    public get invalid(): boolean {
        return this.formGroup.invalid;
    }

    private initRevisedNatureOfComplaintChange(): void {
        this.formGroup.controls.natureOfComplaint.valueChanges
            .pipe(
                filter(value => !!value),
                distinctUntilChanged(),
                tap((value: NatureOfComplaintDomestic | 'NO_CHANGE') => {
                    if (!this.disabled) {
                        this.formGroup.controls.issueType.reset();
                        this.formGroup.controls.issueType.enable();
                    }

                    if (value === 'NO_CHANGE') {
                        this.options = Object.values(RootCause);
                        this.label = 'Root Cause';
                        return;
                    }

                    this.options = this.disabled
                        ? config[value].options
                        : config[value].options.filter(opt => !config[value].hidden?.includes(opt));
                    this.label = config[value].label;
                })
            )
            .subscribe();
    }
}
